import type { PriceSubmit } from '@/features/menu/components/PriceSheetContent';
import { fail } from '@/features/menu/utils/product-page-actions';
import { useT } from '@/i18n';
import { formatMoney, inputToMinor, moneyToInput } from '@/utils/money';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { useEffect, useState } from 'react';
import { Block, BlockFooter, Button, List, ListInput, PageContent } from 'framework7-react';

// ─── Per-channel pricing ─────────────────────────────────────────────────────

export type ClearFn = (done: () => void, errOut: (e: unknown) => void) => void;

export default function ChannelPriceSheetContent({
  open,
  onClose,
  base,
  initial,
  hasOverride,
  onSubmit,
  onClear,
  submitRef,
}: {
  open: boolean;
  onClose: () => void;
  base: number;
  initial: number;
  hasOverride: boolean;
  onSubmit: PriceSubmit;
  onClear: ClearFn;
  submitRef: { current: () => void };
}) {
  const t = useT();
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const [value, setValue] = useState('0');
  useEffect(() => {
    if (open) setValue(moneyToInput(initial, currency));
  }, [open, initial, currency]);

  useUnsavedGuard(open && value !== moneyToInput(initial, currency));

  submitRef.current = () => onSubmit(inputToMinor(value, currency), onClose, fail);

  return (
    <PageContent>
      <List strongIos insetIos>
        <ListInput
          label={t.menu.productForm.channelPrice}
          type="number"
          inputmode="decimal"
          value={value}
          onInput={(e) => setValue((e.target as HTMLInputElement).value)}
        />
      </List>
      <BlockFooter>{t.menu.overridesBaseFooter(formatMoney(base, currency))}</BlockFooter>
      {hasOverride && (
        <Block>
          <Button onClick={() => onClear(onClose, fail)}>{t.menu.useBasePrice}</Button>
        </Block>
      )}
    </PageContent>
  );
}
